import React from 'react'
import { Button, Typography, Row, Col, Card, Space, Divider } from 'antd'
import { 
  CheckCircleOutlined, 
  MobileOutlined, 
  SafetyCertificateOutlined,
  ClockCircleOutlined,
  DollarOutlined,
  FileTextOutlined,
  TeamOutlined,
  RocketOutlined,
  StarOutlined
} from '@ant-design/icons'
import { useNavigate } from 'react-router-dom'

const { Title, Paragraph, Text } = Typography

export const LandingPage: React.FC = () => {
  const navigate = useNavigate()

  const features = [
    {
      icon: <MobileOutlined style={{ fontSize: '32px', color: '#1890ff' }} />,
      title: 'Real-Time Job Tracking',
      description: 'Track your service requests in real-time with live updates from technicians in the field.'
    },
    {
      icon: <DollarOutlined style={{ fontSize: '32px', color: '#52c41a' }} />,
      title: 'Instant Invoice Payment',
      description: 'Pay invoices securely online with multiple payment options and automatic receipts.'
    },
    {
      icon: <FileTextOutlined style={{ fontSize: '32px', color: '#fa8c16' }} />,
      title: 'Document Management',
      description: 'Upload photos, documents, and communicate directly with your service team.'
    },
    {
      icon: <SafetyCertificateOutlined style={{ fontSize: '32px', color: '#722ed1' }} />,
      title: 'Secure & Private',
      description: 'Your data is protected with enterprise-grade security and privacy controls.'
    },
    {
      icon: <ClockCircleOutlined style={{ fontSize: '32px', color: '#eb2f96' }} />,
      title: '24/7 Access',
      description: 'Access your service history, schedules, and invoices anytime, anywhere.'
    },
    {
      icon: <TeamOutlined style={{ fontSize: '32px', color: '#13c2c2' }} />,
      title: 'Direct Communication',
      description: 'Chat directly with technicians and customer service through the portal.'
    }
  ]

  const testimonials = [
    {
      name: 'Sarah Johnson',
      company: 'Homeowner',
      text: 'The customer portal makes it so easy to track my HVAC maintenance. I love getting real-time updates!',
      rating: 5
    },
    {
      name: 'Mike Chen',
      company: 'Property Manager',
      text: 'Managing service requests for 20+ properties has never been easier. The portal saves me hours every week.',
      rating: 5
    },
    {
      name: 'Lisa Rodriguez',
      company: 'Business Owner',
      text: 'Being able to pay invoices instantly and track service history is a game-changer for our operations.',
      rating: 5
    }
  ]

  return (
    <div style={{ minHeight: '100vh' }}>
      {/* Hero Section */}
      <div style={{ 
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        padding: '80px 20px',
        textAlign: 'center',
        color: 'white'
      }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <Title level={1} style={{ color: 'white', fontSize: '48px', marginBottom: '24px' }}>
            Revolutionary Customer Portal
          </Title>
          <Title level={2} style={{ color: 'white', fontWeight: 300, marginBottom: '32px' }}>
            Powered by ServiceFlow Pro
          </Title>
          <Paragraph style={{ fontSize: '20px', color: 'rgba(255,255,255,0.9)', marginBottom: '48px', maxWidth: '600px', margin: '0 auto 48px' }}>
            Experience the future of service management with our cutting-edge customer portal. 
            Track jobs, pay invoices, and communicate with technicians - all in real-time.
          </Paragraph>
          <Space size="large">
            <Button 
              type="primary" 
              size="large" 
              onClick={() => navigate('/login')}
              style={{ 
                height: '50px', 
                padding: '0 32px', 
                fontSize: '16px',
                background: 'rgba(255,255,255,0.2)',
                border: '2px solid white',
                color: 'white'
              }}
            >
              Access Your Portal
            </Button>
            <Button 
              size="large" 
              onClick={() => window.open('https://serviceflow-pro.com', '_blank')}
              style={{ 
                height: '50px', 
                padding: '0 32px', 
                fontSize: '16px',
                background: 'transparent',
                border: '2px solid white',
                color: 'white'
              }}
            >
              Learn About ServiceFlow
            </Button>
          </Space>
        </div>
      </div>

      {/* Features Section */}
      <div style={{ padding: '80px 20px', background: '#f8f9fa' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: '64px' }}>
            <Title level={2}>Why Customers Love Our Portal</Title>
            <Paragraph style={{ fontSize: '18px', color: '#666', maxWidth: '600px', margin: '0 auto' }}>
              Built with modern technology and designed for the ultimate user experience
            </Paragraph>
          </div>
          
          <Row gutter={[32, 32]}>
            {features.map((feature, index) => (
              <Col xs={24} md={12} lg={8} key={index}>
                <Card 
                  style={{ 
                    height: '100%', 
                    textAlign: 'center',
                    border: 'none',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                    borderRadius: '12px'
                  }}
                  styles={{ body: { padding: '32px 24px' } }}
                >
                  <div style={{ marginBottom: '16px' }}>
                    {feature.icon}
                  </div>
                  <Title level={4} style={{ marginBottom: '16px' }}>
                    {feature.title}
                  </Title>
                  <Paragraph style={{ color: '#666', margin: 0 }}>
                    {feature.description}
                  </Paragraph>
                </Card>
              </Col>
            ))}
          </Row>
        </div>
      </div>

      {/* Stats Section */}
      <div style={{ padding: '80px 20px', background: 'white' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto', textAlign: 'center' }}>
          <Title level={2} style={{ marginBottom: '48px' }}>
            Trusted by Service Professionals Everywhere
          </Title>
          
          <Row gutter={[32, 32]}>
            <Col xs={24} sm={8}>
              <div>
                <Title level={1} style={{ color: '#1890ff', marginBottom: '8px' }}>
                  10,000+
                </Title>
                <Text style={{ fontSize: '18px', color: '#666' }}>
                  Active Customers
                </Text>
              </div>
            </Col>
            <Col xs={24} sm={8}>
              <div>
                <Title level={1} style={{ color: '#52c41a', marginBottom: '8px' }}>
                  500+
                </Title>
                <Text style={{ fontSize: '18px', color: '#666' }}>
                  Service Businesses
                </Text>
              </div>
            </Col>
            <Col xs={24} sm={8}>
              <div>
                <Title level={1} style={{ color: '#fa8c16', marginBottom: '8px' }}>
                  99.9%
                </Title>
                <Text style={{ fontSize: '18px', color: '#666' }}>
                  Uptime Guarantee
                </Text>
              </div>
            </Col>
          </Row>
        </div>
      </div>

      {/* Testimonials Section */}
      <div style={{ padding: '80px 20px', background: '#f8f9fa' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: '64px' }}>
            <Title level={2}>What Our Customers Say</Title>
          </div>
          
          <Row gutter={[32, 32]}>
            {testimonials.map((testimonial, index) => (
              <Col xs={24} lg={8} key={index}>
                <Card 
                  style={{ 
                    height: '100%',
                    border: 'none',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                    borderRadius: '12px'
                  }}
                  styles={{ body: { padding: '32px' } }}
                >
                  <div style={{ marginBottom: '16px' }}>
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <StarOutlined key={i} style={{ color: '#faad14', marginRight: '4px' }} />
                    ))}
                  </div>
                  <Paragraph style={{ fontSize: '16px', fontStyle: 'italic', marginBottom: '24px' }}>
                    "{testimonial.text}"
                  </Paragraph>
                  <div>
                    <Text strong>{testimonial.name}</Text>
                    <br />
                    <Text type="secondary">{testimonial.company}</Text>
                  </div>
                </Card>
              </Col>
            ))}
          </Row>
        </div>
      </div>

      {/* CTA Section */}
      <div style={{ 
        background: 'linear-gradient(135deg, #1890ff 0%, #722ed1 100%)',
        padding: '80px 20px',
        textAlign: 'center',
        color: 'white'
      }}>
        <div style={{ maxWidth: '800px', margin: '0 auto' }}>
          <RocketOutlined style={{ fontSize: '64px', marginBottom: '24px' }} />
          <Title level={2} style={{ color: 'white', marginBottom: '16px' }}>
            Ready to Transform Your Service Experience?
          </Title>
          <Paragraph style={{ fontSize: '18px', color: 'rgba(255,255,255,0.9)', marginBottom: '32px' }}>
            Join thousands of satisfied customers who have revolutionized how they manage their service needs.
          </Paragraph>
          <Space size="large">
            <Button 
              type="primary" 
              size="large" 
              onClick={() => navigate('/login')}
              style={{ 
                height: '50px', 
                padding: '0 32px', 
                fontSize: '16px',
                background: 'white',
                border: 'none',
                color: '#1890ff'
              }}
            >
              Get Started Today
            </Button>
          </Space>
        </div>
      </div>

      {/* Footer */}
      <div style={{ 
        background: '#001529', 
        padding: '40px 20px', 
        textAlign: 'center',
        color: 'rgba(255,255,255,0.65)'
      }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <Text style={{ color: 'rgba(255,255,255,0.65)' }}>
            © 2025 ServiceFlow Pro. All rights reserved. | Revolutionizing service management worldwide.
          </Text>
          <br />
          <Space split={<Divider type="vertical" />} style={{ marginTop: '16px' }}>
            <Button type="link" style={{ color: 'rgba(255,255,255,0.65)' }}>
              Privacy Policy
            </Button>
            <Button type="link" style={{ color: 'rgba(255,255,255,0.65)' }}>
              Terms of Service
            </Button>
            <Button type="link" style={{ color: 'rgba(255,255,255,0.65)' }}>
              Contact Support
            </Button>
          </Space>
        </div>
      </div>
    </div>
  )
}
